/**
 * Shared icon maps for marketplace atoms.
 *
 * Previously each chip/pill/glyph atom carried its own copy of these maps and
 * they had drifted (the three SCOPE icon constants disagreed on which PrimeIcon
 * represents `center` and `all`, producing visibly different icons for the
 * same scope across the UI). Centralizing the maps fixes that and removes
 * three verbatim copies of `ITEM_TYPE_ICON_MAP`.
 */

import type { MarketplaceItemType } from './ItemTypeGlyphAtom';
import type { TagTone } from '../Tag/TagAtom';

export const MARKETPLACE_ITEM_TYPE_ICONS: Record<MarketplaceItemType, string> = {
  approach: 'lucide:compass',
  video: 'lucide:video',
  metric_library: 'lucide:bar-chart-3',
  evaluation: 'lucide:clipboard-check',
  assistant: 'lucide:message-square',
  form: 'lucide:file-pen-line',
  schema: 'lucide:database',
  context: 'lucide:layers',
  arc_template: 'lucide:route',
  orbit_template: 'lucide:orbit',
  metaspace: 'lucide:layout-grid',
  aspect: 'lucide:tag',
  aspect_group: 'lucide:tags',
  composition: 'lucide:puzzle',
};

/** TagAtom tone for each marketplace item type — matches the per-type
 * tinting that the deleted ItemTypeChipAtom encoded in its SCSS. */
export const MARKETPLACE_ITEM_TYPE_TONES: Record<MarketplaceItemType, TagTone> = {
  approach: 'info',
  video: 'danger',
  metric_library: 'success',
  evaluation: 'warn',
  assistant: 'accent',
  form: 'teal',
  schema: 'neutral',
  context: 'success',
  arc_template: 'warn',
  orbit_template: 'info',
  metaspace: 'accent',
  aspect: 'outline',
  aspect_group: 'teal',
  composition: 'teal',
};

export type MarketplaceScope = 'center' | 'organization' | 'metasphere' | 'all';

export const MARKETPLACE_SCOPE_ICONS: Record<MarketplaceScope, string> = {
  center: 'lucide:building-2',
  organization: 'lucide:network',
  metasphere: 'lucide:globe',
  all: 'lucide:layout-grid',
};

/** TagAtom tone for each marketplace scope. `all` is treated as neutral
 * since it represents "no filter applied" rather than a specific scope. */
export const MARKETPLACE_SCOPE_TONES: Record<MarketplaceScope, TagTone> = {
  center: 'info',
  organization: 'accent',
  metasphere: 'teal',
  all: 'neutral',
};
